/* eslint-disable no-console */
import NotificationSubscriptions from '../api/notificationSubscription';
import auth from '../api/auth';
import { useAlert } from 'dashboard/composables';
import {
  firebaseWebConfig,
  registerFirebasePush,
  unregisterFirebasePush,
} from './firebasePush';

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return Promise.resolve(null);
  }

  if (!('PushManager' in window)) {
    // Push isn't supported on this browser, disable or hide UI.
    return Promise.resolve(null);
  }

  return navigator.serviceWorker
    .register(firebaseWebConfig() ? '/firebase-messaging-sw.js' : '/sw.js')
    .then(registration => {
      callback(registration);
      return registration;
    })
    .catch(registrationError => {
      // eslint-disable-next-line
      console.log('SW registration failed: ', registrationError);
      return null;
    });
};

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

export const sendRegistrationToServer = subscription => {
  if (auth.hasAuthCookie()) {
    return NotificationSubscriptions.create(
      getPushSubscriptionPayload(subscription)
    );
  }
  return null;
};

export const registerSubscription = (
  onSuccess = () => {},
  onError = () => {}
) => {
  if (!auth.hasAuthCookie() || !hasPushPermissions()) return Promise.resolve();
  if (!firebaseWebConfig() && !window.chatwootConfig.vapidPublicKey) {
    return Promise.resolve();
  }
  return verifyServiceWorkerExistence()
    .then(registration => {
      if (!registration) throw new Error('Service worker indisponível.');
      return navigator.serviceWorker.ready;
    })
    .then(serviceWorkerRegistration => {
      if (firebaseWebConfig())
        return registerFirebasePush(serviceWorkerRegistration);
      return serviceWorkerRegistration.pushManager
        .subscribe({
          userVisibleOnly: true,
          applicationServerKey: window.chatwootConfig.vapidPublicKey,
        })
        .then(sendRegistrationToServer);
    })
    .then(() => {
      onSuccess();
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('Push subscription registration failed:', error);
      useAlert('Não foi possível ativar as notificações neste navegador.');
      onError();
    });
};

export const unregisterSubscription = async () => {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration('/');
  const subscription = await registration?.pushManager.getSubscription();
  if (subscription) await subscription.unsubscribe();
  let timeoutId;
  try {
    await Promise.race([
      Promise.all([
        unregisterFirebasePush(),
        subscription && auth.hasAuthCookie()
          ? NotificationSubscriptions.removeSubscription({
              endpoint: subscription.endpoint,
            })
          : Promise.resolve(),
      ]),
      new Promise((resolve, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error('Push cleanup timed out')),
          5000
        );
      }),
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
};

export const requestPushPermissions = ({ onSuccess, onError = () => {} }) => {
  if (!('Notification' in window)) {
    // eslint-disable-next-line no-console
    console.warn('Notification is not supported');
    useAlert('Este navegador não oferece suporte a notificações.');
    onError();
  } else if (Notification.permission === 'granted') {
    registerSubscription(onSuccess, onError);
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission().then(permission => {
      if (permission === 'granted') {
        registerSubscription(onSuccess, onError);
      } else {
        onError();
      }
    });
  } else {
    useAlert('Permita as notificações nas configurações do navegador.');
    onError();
  }
};
